import SpeechRecognizer from './speech-recognizer.ts';
import { UIManager } from './ui-manager.js';

export class SpeechManager {
  private speechRecognizer: SpeechRecognizer | null = null;
  private uiManager: UIManager;
  private onTranscriptionComplete?: (text: string | null, fullResult: any) => void;
  private onTranscriptionUpdate?: (text: string | null, isFinal: boolean, confidence: number) => void;
  private onError?: (error: string, shouldRetry: boolean) => void;
  private consecutiveErrors: number = 0;

  constructor(uiManager: UIManager) {
    this.uiManager = uiManager;
  }

  /**
    * Set callbacks
    */
   setCallbacks(
     onTranscriptionComplete: (text: string | null, fullResult: any) => void,
     onTranscriptionUpdate: (text: string | null, isFinal: boolean, confidence: number) => void,
     onError: (error: string, shouldRetry: boolean) => void
   ): void {
     this.onTranscriptionComplete = onTranscriptionComplete;
     this.onTranscriptionUpdate = onTranscriptionUpdate;
     this.onError = onError;
   }

 /**
   * Initialize speech recognition (Web Speech API only)
   */
 async initializeSpeechRecognition(): Promise<boolean> {
   const support = SpeechRecognizer.checkSupport();
   if (!support.supported) {
     throw new Error('Web Speech API not supported in this browser');
   }

   console.log('Initializing Web Speech API...');
   this.speechRecognizer = new SpeechRecognizer();
   this.setupSpeechCallbacks();

   const initialized = await this.speechRecognizer.initialize();
   if (!initialized) {
     throw new Error('Failed to initialize Web Speech API');
   }

   console.log('Web Speech API initialized successfully');
   return true;
 }

  /**
   * Setup speech recognizer callbacks
   */
  private setupSpeechCallbacks(): void {
    if (!this.speechRecognizer) return;

    this.speechRecognizer.onModelLoading = (modelName: string) => {
      this.uiManager.setStatus('initializing', `Loading ${modelName}...`);
    };

    this.speechRecognizer.onModelLoaded = () => {
      this.uiManager.setStatus('idle', 'Ready');
    };

    this.speechRecognizer.onTranscriptionStart = () => {
      this.uiManager.setStatus('recording', 'Listening for your answer...');
      this.uiManager.setRecordingVisual(true);
      console.log('Started listening for speech');
    };

    this.speechRecognizer.onTranscriptionComplete = (text: string | null, fullResult: any) => {
       console.log(`Speech transcribed: "${text}"`);
       this.uiManager.setRecordingVisual(false);
       this.uiManager.setStatus('processing', 'Processing your answer...');
       this.consecutiveErrors = 0; // Reset error counter on successful transcription
       if (this.onTranscriptionComplete) {
         this.onTranscriptionComplete(text, fullResult);
       }
     };

     this.speechRecognizer.onTranscriptionUpdate = (text: string | null, isFinal: boolean, confidence: number) => {
       if (this.onTranscriptionUpdate) {
         this.onTranscriptionUpdate(text, isFinal, confidence);
       }
     };

    this.speechRecognizer.onError = (error: string, shouldRetry: boolean) => {
      console.error('Speech recognition error:', error);
      this.uiManager.setRecordingVisual(false);
      this.consecutiveErrors++;

      if (this.onError) {
        this.onError(error, shouldRetry);
      }
    };
  }

  /**
   * Check if ready
   */
  isReady(): boolean {
    return this.speechRecognizer ? this.speechRecognizer.isReady() : false;
  }

  /**
   * Start listening for user response with optional answer validator
   * @param answerValidator - Optional callback to check if transcript matches correct answer
   */
  async startListening(answerValidator?: (transcript: string) => boolean): Promise<void> {
    if (!this.speechRecognizer) {
      throw new Error('Speech recognizer not initialized');
    }

    // Set the answer validator if provided
    if (answerValidator) {
      this.speechRecognizer.setAnswerValidator(answerValidator);
    } else {
      this.speechRecognizer.setAnswerValidator(null);
    }

    console.log('Starting to listen for user response...');

    try {
      await this.speechRecognizer.transcribe(null);
    } catch (error) {
      console.error('Failed to start listening:', error);
      this.consecutiveErrors++;
      throw error;
    } finally {
      // Clear the validator after listening completes
      if (this.speechRecognizer) {
        this.speechRecognizer.setAnswerValidator(null);
      }
    }
  }

  /**
   * Stop listening
   */
  stopListening(): void {
    if (this.speechRecognizer) {
      this.speechRecognizer.stopListening();
    }
    this.uiManager.setRecordingVisual(false);
  }

  /**
   * Get consecutive errors
   */
  getConsecutiveErrors(): number {
    return this.consecutiveErrors;
  }

  /**
   * Reset consecutive errors
   */
  resetConsecutiveErrors(): void {
    this.consecutiveErrors = 0;
  }

  /**
   * Cleanup
   */
  cleanup(): void {
    if (this.speechRecognizer) {
      this.speechRecognizer.cleanup();
    }
  }
}